```
<template>
  <div class="flex h-[calc(100vh-4rem)] lg:h-screen overflow-hidden bg-gray-50">
    <!-- Sidebar: Adventurers List -->
    <div class="w-80 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
      <div class="p-4 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-800">Adventurers</h2>
        <p class="text-xs text-gray-500 mt-1">Select an adventurer to view notes</p>
      </div>
      
      <div class="flex-1 overflow-y-auto">
        <div v-if="isLoadingAdventurers" class="p-4 flex justify-center">
          <Loader2 class="w-6 h-6 animate-spin text-gray-400" />
        </div>
        <div v-else-if="sortedUsers.length === 0" class="p-4 text-center text-gray-500 text-sm">
          No adventurers found.
        </div>
        <ul v-else class="divide-y divide-gray-100">
          <!-- Batch Overview Item -->
          <li 
            @click="selectBatchOverview"
            class="cursor-pointer hover:bg-gray-50 transition-colors duration-150"
            :class="{ 'bg-blue-50 hover:bg-blue-50': isBatchSelected }"
          >
            <div class="p-4 flex items-center space-x-3">
              <div class="flex-shrink-0">
                <div class="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium text-sm">
                  <Users class="w-5 h-5" />
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 truncate">
                  Batch Overview
                </p>
                <p class="text-xs text-gray-500 truncate">
                  All adventurers
                </p>
              </div>
              <div v-if="isBatchSelected">
                <ChevronRight class="w-4 h-4 text-blue-500" />
              </div>
            </div>
          </li>

          <!-- Adventurers List -->
          <li 
            v-for="user in sortedUsers" 
            :key="user.id"
            @click="selectAdventurer(user)"
            class="cursor-pointer hover:bg-gray-50 transition-colors duration-150"
            :class="{ 'bg-blue-50 hover:bg-blue-50': selectedAdventurer?.id === user.id }"
          >
            <div class="p-4 flex items-center space-x-3">
              <div class="flex-shrink-0">
                <div v-if="user.avatar_image" class="w-10 h-10 rounded-full overflow-hidden">
                  <img
                    :src="user.avatar_image"
                    alt=""
                    class="w-full h-full object-cover"
                    :class="{ 'blur-md select-none': isBlurred }"
                  />
                </div>
                <div v-else class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium text-sm overflow-hidden">
                  <span :class="{ 'blur-md select-none': isBlurred }">
                    {{ getInitials(user) }}
                  </span>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 truncate" :class="{ 'blur-md select-none': isBlurred }">
                  {{ user.first_name }} {{ user.last_name }}
                </p>
                <p class="text-xs text-gray-500 truncate" :class="{ 'blur-md select-none': isBlurred }">
                  {{ user.email }}
                </p>
              </div>
              <div v-if="selectedAdventurer?.id === user.id">
                <ChevronRight class="w-4 h-4 text-blue-500" />
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <!-- Main Content: Notes -->
    <div class="flex-1 flex flex-col min-w-0 overflow-hidden">
      <div v-if="selectedAdventurer || isBatchSelected" class="flex-1 flex flex-col h-full">
        <!-- Header with Stats -->
        <div class="bg-white border-b border-gray-200 shadow-sm z-10">
          <div class="px-4 py-2 flex justify-between items-center">
            <div class="flex gap-2 items-center">
              <template v-if="isBatchSelected">
                <div class="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium text-sm">
                  <Users class="w-8 h-8" />
                </div>
                <div>
                  <h1 class="text-2xl text-gray-900">
                    Batch Overview
                  </h1>
                  <p class="text-sm text-gray-500 mt-1">
                    {{ formattedExpeditionName }}
                  </p>
                </div>
              </template>
              <template v-else>
                <div v-if="selectedAdventurer.avatar_image" class="w-16 h-16 rounded-full overflow-hidden">
                  <img
                    :src="selectedAdventurer.avatar_image"
                    alt="Avatar"
                    class="w-full h-full object-cover"
                    :class="{ 'blur-md select-none': isBlurred }"
                  />
                </div>
                <div v-else class="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium text-sm overflow-hidden">
                  <span :class="{ 'blur-md select-none': isBlurred }">
                    {{ getInitials(selectedAdventurer) }}
                  </span>
                </div>
                <div>
                  <div class="flex items-center gap-1">
                    <h1 class="text-2xl text-gray-900" :class="{ 'blur-md select-none': isBlurred }">
                      {{ selectedAdventurer.first_name }} {{ selectedAdventurer.last_name }}
                    </h1>
                    <a v-if="!isBlurred" :href="`https://outtalent.com/profile/${selectedAdventurer.username}`" target="_blank" class="text-gray-500 rounded-full hover:bg-gray-200 p-1 transition-all">
                      <CircleUser class="w-6 h-6" />
                    </a>
                  </div>
                  <p class="text-sm text-gray-500 mt-1">
                    {{ formattedExpeditionName }}
                  </p>
                </div>
              </template>
            </div>
            
            <!-- Progress Stats -->
            <ProgressStats v-if="!isBatchSelected" :user-data="selectedAdventurer" :minimized="true"/>
          </div>
        </div>

        <!-- Notes List -->
        <div class="flex-1 overflow-y-auto p-6 bg-gray-50">
          <div class="max-w-4xl mx-auto space-y-6">
            
            <!-- Inline Add Note Form -->
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div 
                class="px-6 py-4 border-b border-gray-100 bg-gray-50 hover:bg-white transition-colors duration-200 flex justify-between items-center cursor-pointer" 
                @click="isAddExpanded = !isAddExpanded"
              >
                <h3 class="text-sm font-medium text-gray-900 flex items-center">
                  <Plus class="w-4 h-4 mr-2 text-blue-600" />
                  Add New Note
                </h3>
                <ChevronRight 
                  class="w-4 h-4 text-gray-400 transition-transform duration-300"
                  :class="{ 'rotate-90': isAddExpanded }"
                />
              </div>
              
              <div 
                class="transition-all duration-300 ease-in-out overflow-hidden"
                :class="isAddExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'"
              >
                <div class="p-6">
                  <div class="space-y-4">
                    <!-- Week Selection -->
                    <div class="w-64">
                      <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Week</label>
                      <div class="flex space-x-2">
                        <select 
                          v-model="noteForm.weekSelection" 
                          class="block w-full pl-3 pr-8 py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md border"
                        >
                          <option v-for="week in availableWeeks" :key="week" :value="week">
                            Week {{ week }}
                          </option>
                          <option value="custom">Custom Week</option>
                        </select>
                        <input 
                          v-if="noteForm.weekSelection === 'custom'"
                          type="number" 
                          v-model="noteForm.customWeek"
                          class="block w-20 pl-3 pr-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md border"
                          placeholder="#"
                          min="1"
                        />
                      </div>
                    </div>

                    <!-- Note Content -->
                    <div>
                      <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Note Content</label>
                      <textarea 
                        v-model="noteForm.note" 
                        rows="6" 
                        class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md border p-3"
                        placeholder="Enter your observations and feedback here..."
                      ></textarea>
                    </div>

                    <!-- Validation Error -->
                    <div v-if="noteFormError" class="bg-red-50 border border-red-200 rounded-md p-3">
                      <p class="text-sm text-red-600">{{ noteFormError }}</p>
                    </div>

                    <div class="flex justify-end pt-2">
                      <button 
                        type="button" 
                        class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm text-white bg-black hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        @click="saveNote"
                        :disabled="isSaving || !noteForm.note.trim()"
                      >
                        <Loader2 v-if="isSaving" class="w-4 h-4 mr-2 animate-spin" />
                        {{ isSaving ? 'Saving...' : 'Save Note' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Notes List Content -->
            <div v-if="isLoadingNotes" class="flex justify-center items-center h-32">
              <Loader2 class="w-8 h-8 animate-spin text-gray-400" />
            </div>
            
            <div v-else-if="adventurerNotes.length === 0" class="text-center py-12">
              <p class="text-gray-500 text-sm">
                {{ isBatchSelected 
                  ? 'No expedition-wide notes yet. Add a note or click on a fellow to view their notes.' 
                  : 'No notes found for this adventurer.' 
                }}
              </p>
            </div>

            <div v-else class="space-y-4">
              <div 
                v-for="note in adventurerNotes" 
                :key="note.id" 
                class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                <!-- View Mode -->
                <div v-if="editingNoteId !== note.id">
                  <div class="px-6 py-3 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                    <div class="flex items-center space-x-3">
                      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Week {{ note.week }}
                      </span>
                      <span class="text-xs text-gray-500">
                        {{ formatDate(note.created_at) }}
                      </span>
                    </div>
                    <div class="flex space-x-1">
                      <button 
                        @click="startEditing(note)"
                        class="text-gray-400 hover:text-blue-600 transition-colors p-1.5 rounded-full hover:bg-blue-50"
                        title="Edit Note"
                      >
                        <Pencil class="w-3.5 h-3.5" />
                      </button>
                      <button 
                        @click="confirmDeleteNote(note)"
                        class="text-gray-400 hover:text-red-600 transition-colors p-1.5 rounded-full hover:bg-red-50"
                        title="Delete Note"
                      >
                        <Trash2 class="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div 
                    class="px-6 py-4 cursor-pointer hover:bg-blue-50/50 transition-colors group"
                    @click="startEditing(note)"
                    title="Click to edit"
                  >
                    <p class="text-gray-700 whitespace-pre-wrap leading-relaxed text-sm group-hover:text-gray-900">{{ note.note }}</p>
                  </div>
                </div>

                <!-- Edit Mode -->
                <div v-else>
                  <div class="px-6 py-3 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                    <div class="flex items-center space-x-3">
                      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Week {{ note.week }}
                      </span>
                      <span class="text-xs text-gray-500">
                        {{ formatDate(note.created_at) }}
                      </span>
                    </div>
                    <div class="flex items-center space-x-2">
                      <button 
                        @click="cancelEditing"
                        class="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        Cancel
                      </button>
                      <button 
                        @click="saveEdit(note)"
                        class="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium text-white bg-black hover:bg-black/80 transition-colors"
                        :disabled="isSaving"
                      >
                        <Loader2 v-if="isSaving" class="w-3 h-3 mr-1 animate-spin" />
                        Save
                      </button>
                    </div>
                  </div>
                  <div class="px-6 py-4">
                    <textarea 
                      v-model="editForm.note" 
                      rows="6" 
                      class="block w-full text-gray-700 text-sm leading-relaxed resize-none bg-transparent border-0 p-0 focus:ring-0 focus:outline-none"
                      style="min-height: 1.5em;"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State (No Adventurer Selected) -->
      <div v-else class="flex-1 flex flex-col items-center justify-center bg-gray-50 text-gray-500">
        <div class="w-16 h-16 bg-white rounded-full shadow-sm flex items-center justify-center mb-4">
          <Users class="w-8 h-8 text-gray-400" />
        </div>
        <h3 class="text-lg font-medium text-gray-900">No adventurer selected</h3>
        <p class="text-sm mt-1">Select an adventurer from the sidebar to view and manage their notes.</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useExpeditionsStore } from '@/stores/expeditions'
import { useCurriculumStore } from '@/stores/curriculum'
import { notesService } from '@/services/notes'
import { formatExpeditionName } from '@/utils/formatExpeditionName'
import ProgressStats from '@/components/dashboard/ProgressStats.vue'
import { usePrivacyMode } from '@/composables/usePrivacyMode'
import { 
  Loader2, 
  ChevronRight, 
  Plus, 
  Users, 
  Pencil, 
  Trash2,
  CircleUser 
} from 'lucide-vue-next'

// Stores and Route
const route = useRoute()
const expeditionsStore = useExpeditionsStore()
const curriculumStore = useCurriculumStore()
const expeditionId = computed(() => route.params.id as string)
const { isBlurred } = usePrivacyMode()

// State
const isLoadingAdventurers = ref(false)
const isLoadingNotes = ref(false)
const isSaving = ref(false)
const selectedAdventurer = ref<any>(null)
const isBatchSelected = ref(false)
const adventurerNotes = ref<any[]>([])

// Add Note State
const isAddExpanded = ref(false)
const noteForm = ref({
  weekSelection: 1 as number | 'custom',
  customWeek: null as number | null,
  note: ''
})
const noteFormError = ref<string | null>(null)

// Edit Note State
const editingNoteId = ref<number | null>(null)
const editForm = ref({
  note: ''
})

// Computed
const formattedExpeditionName = computed(() => {
  const progress = expeditionsStore.adventurersProgress as any
  return formatExpeditionName(progress?.name)
})

const sortedUsers = computed(() => {
  if (!expeditionsStore.adventurersProgress?.result) return []
  return [...expeditionsStore.adventurersProgress.result].sort((a, b) => {
    // Sort by name for easier finding in list
    const nameA = `${a.first_name} ${a.last_name}`.toLowerCase()
    const nameB = `${b.first_name} ${b.last_name}`.toLowerCase()
    return nameA.localeCompare(nameB)
  })
})

const currentWeek = computed(() => {
  return curriculumStore.activeWeek?.current_week || 1
})

const availableWeeks = computed(() => {
  // Create array from 1 to currentWeek + 1 (allow planning ahead slightly)
  const maxWeek = Math.max(currentWeek.value, 1)
  return Array.from({ length: maxWeek }, (_, i) => i + 1).reverse()
})

// Methods
const getInitials = (user: any) => {
  return `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase()
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const selectAdventurer = async (user: any) => {
  selectedAdventurer.value = user
  isBatchSelected.value = false
  // Reset forms
  isAddExpanded.value = false
  noteForm.value = { weekSelection: currentWeek.value, customWeek: null, note: '' }
  noteFormError.value = null
  cancelEditing()
  await fetchNotes()
}

const selectBatchOverview = async () => {
  selectedAdventurer.value = null
  isBatchSelected.value = true
  // Reset forms
  isAddExpanded.value = false
  noteForm.value = { weekSelection: currentWeek.value, customWeek: null, note: '' }
  noteFormError.value = null
  cancelEditing()
  await fetchNotes()
}

const fetchNotes = async () => {
  if (!selectedAdventurer.value && !isBatchSelected.value) return
  
  isLoadingNotes.value = true
  try {
    const notes = await notesService.getNotesByExpeditionAndAdventurer(
      expeditionId.value,
      isBatchSelected.value ? undefined : selectedAdventurer.value.id
    )
    // Sort by week descending, then created_at descending
    adventurerNotes.value = notes.sort((a: any, b: any) => {
      if (b.week !== a.week) return b.week - a.week
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })
  } catch (error) {
    console.error('Error fetching notes:', error)
  } finally {
    isLoadingNotes.value = false
  }
}

const saveNote = async () => {
  if ((!selectedAdventurer.value && !isBatchSelected.value) || !noteForm.value.note.trim()) return
  
  // Clear previous error
  noteFormError.value = null
  
  const weekToSave = noteForm.value.weekSelection === 'custom' 
    ? noteForm.value.customWeek 
    : noteForm.value.weekSelection

  if (!weekToSave) {
    noteFormError.value = 'Please select a valid week'
    return
  }

  // Check if a note for this week already exists
  const existingNote = adventurerNotes.value.find(note => note.week === Number(weekToSave))
  if (existingNote) {
    noteFormError.value = `A note for Week ${weekToSave} already exists. Please edit the existing note or select a different week.`
    return
  }

  isSaving.value = true
  try {
    await notesService.createNote({
      adventurer: isBatchSelected.value ? undefined : selectedAdventurer.value.id,
      expedition: expeditionId.value,
      week: Number(weekToSave),
      note: noteForm.value.note,
      is_expedition_wide: isBatchSelected.value
    })
    
    // Reset form
    noteForm.value.note = ''
    // Keep the week selection as is for convenience
    
    await fetchNotes()
  } catch (error) {
    console.error('Error saving note:', error)
  } finally {
    isSaving.value = false
  }
}

const startEditing = (note: any) => {
  editingNoteId.value = note.id
  editForm.value.note = note.note
}

const cancelEditing = () => {
  editingNoteId.value = null
  editForm.value.note = ''
}

const saveEdit = async (note: any) => {
  if (!editForm.value.note.trim()) return
  
  isSaving.value = true
  try {
    await notesService.updateNote(note.id, {
      note: editForm.value.note
    })
    editingNoteId.value = null
    await fetchNotes()
  } catch (error) {
    console.error('Error updating note:', error)
  } finally {
    isSaving.value = false
  }
}

const confirmDeleteNote = async (note: any) => {
  if (confirm('Are you sure you want to delete this note?')) {
    try {
      await notesService.deleteNote(note.id)
      await fetchNotes()
    } catch (error) {
      console.error('Error deleting note:', error)
    }
  }
}

// Initialization
const init = async () => {
  isLoadingAdventurers.value = true
  selectedAdventurer.value = null
  isBatchSelected.value = false
  adventurerNotes.value = []
  try {
    await Promise.all([
      expeditionsStore.fetchAdventurersProgress(expeditionId.value),
      curriculumStore.fetchCurrentWeekProgress(expeditionId.value)
    ])
  } catch (error) {
    console.error('Error initializing:', error)
  } finally {
    isLoadingAdventurers.value = false
  }
}

watch(expeditionId, () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  init()
}, { immediate: true })

// Clear error when week selection changes
watch(() => noteForm.value.weekSelection, () => {
  noteFormError.value = null
})

watch(() => noteForm.value.customWeek, () => {
  noteFormError.value = null
})

// Update title
watch(formattedExpeditionName, (newName) => {
  if (newName) {
    document.title = `${newName} Notes | Outtalent Campus`
  }
}, { immediate: true })
</script>
```
